import { useState } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, Skeleton, Button, Input } from '@/components/ui';
import { formatDate, formatRelativeTime } from '@/lib/utils';
import { Toc } from '@/components/Toc';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import DOMPurify from 'dompurify';
import { remarkWikiLink } from '@/lib/remark-wiki-link';
import { Lock, Calendar, Eye, Clock, FileText } from 'lucide-react';
import api from '@/lib/api';
import 'highlight.js/styles/github.css';

interface SharedPageData {
  page: {
    id: number;
    slug: string;
    title: string;
    content: string;
    author: {
      id: number;
      username: string;
      avatar_url: string | null;
    };
    created_at: string;
    updated_at: string;
    tags: { id: number; name: string }[];
  };
  share_info: {
    expires_at: string | null;
    max_views: number | null;
    view_count: number;
  };
}

export function SharedPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlPassword = searchParams.get('password') || '';

  const [inputPassword, setInputPassword] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['shared-page', token, urlPassword],
    queryFn: async () => {
      const response = await api.get(`/s/${token}`, {
        params: urlPassword ? { password: urlPassword } : undefined,
      });
      return response.data.data as SharedPageData;
    },
    retry: false,
  });

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (inputPassword) {
      setSearchParams({ password: inputPassword });
    }
  };

  // Check for password required error
  const isPasswordRequired = (error as any)?.response?.status === 401;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="max-w-4xl mx-auto space-y-6">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  // Password required screen
  if (isPasswordRequired && !urlPassword) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-8 text-center">
            <Lock className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h2 className="text-xl font-semibold mb-2">需要密码访问</h2>
            <p className="text-muted-foreground mb-6">
              此分享链接需要输入密码才能访问
            </p>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="输入访问密码"
                value={inputPassword}
                onChange={(e) => setInputPassword(e.target.value)}
              />
              <Button type="submit" className="w-full">
                访问页面
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Wrong password
  if (isPasswordRequired && urlPassword) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-8 text-center">
            <Lock className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <h2 className="text-xl font-semibold mb-2">密码错误</h2>
            <p className="text-muted-foreground mb-6">
              您输入的密码不正确
            </p>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <Input
                type="password"
                placeholder="重新输入访问密码"
                value={inputPassword}
                onChange={(e) => setInputPassword(e.target.value)}
              />
              <Button type="submit" className="w-full">
                重试
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Expired or not found
  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-8 text-center">
            <h2 className="text-xl font-semibold mb-2">分享链接无效</h2>
            <p className="text-muted-foreground mb-6">
              {(error as any)?.response?.data?.error?.message || '此分享链接已过期或已被撤销'}
            </p>
            <Link to="/">
              <Button variant="outline">返回首页</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) return null;

  const { page, share_info } = data;
  const sanitizedContent = DOMPurify.sanitize(page.content);

  return (
    <div className="min-h-screen bg-background">
      {/* Minimal header for shared pages */}
      <header className="border-b">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
            <FileText className="h-5 w-5" />
            <span className="font-medium">Wiki</span>
          </Link>
          <span className="text-sm text-muted-foreground">分享预览</span>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-8">
        <div className="flex gap-8">
          <div className="flex-1 min-w-0 space-y-6">
            {/* Share info banner */}
            {share_info.expires_at && (
              <div className="bg-muted rounded-lg p-4 flex items-center gap-2 text-sm">
                <Clock className="h-4 w-4" />
                <span>此分享链接将于 {new Date(share_info.expires_at).toLocaleString()} 过期</span>
              </div>
            )}

            {/* Title */}
            <h1 className="text-3xl font-bold">{page.title}</h1>

            {/* Meta info */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span>作者: {page.author.username}</span>
              <span>•</span>
              <span className="flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                创建于 {formatDate(page.created_at)}
              </span>
              <span>•</span>
              <span>更新于 {formatRelativeTime(page.updated_at)}</span>
              {share_info.max_views && (
                <>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Eye className="h-4 w-4" />
                    {share_info.view_count} / {share_info.max_views} 次访问
                  </span>
                </>
              )}
            </div>

            {/* Tags */}
            {page.tags && page.tags.length > 0 && (
              <div className="flex items-center space-x-2">
                {page.tags.map((tag) => (
                  <span
                    key={tag.id}
                    className="px-3 py-1 bg-muted rounded-full text-sm"
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
            )}

            {/* Content */}
            <div className="prose prose-slate dark:prose-invert max-w-none">
              <div className="markdown-content">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm, remarkWikiLink]}
                  rehypePlugins={[rehypeHighlight, rehypeRaw]}
                >
                  {sanitizedContent}
                </ReactMarkdown>
              </div>
            </div>
          </div>

          {/* TOC sidebar - desktop only */}
          <div className="hidden lg:block shrink-0 w-64">
            <div className="sticky top-8">
              <Toc content={page.content} />
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-6 mt-auto">
        <div className="container mx-auto px-4 text-center text-sm text-muted-foreground">
          <p>Wiki 分享页面</p>
        </div>
      </footer>
    </div>
  );
}
